import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, User } from '../services/api';
import { invalidateConnectorAccess } from '../hooks/useConnectorAccess';
import { invalidateTaskAccess } from '../hooks/useTaskAccess';
import { invalidateWorkflowAccess } from '../hooks/useWorkflowAccess';

/** How often the signed-in user is re-read, besides on load and window focus. */
const USER_REFRESH_MS = 60_000;

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => void;
  clearMustChangePassword: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load user from localStorage
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // The server applies role changes at once; keep the copy the UI reads (admin
  // menus, isAdmin) in step, so nobody has to sign out and back in. Only fields
  // an admin changes are taken: mustChangePassword stays as login set it (SSO
  // logins clear it). A 401 here means the account was deactivated, and the API
  // interceptor signs out.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const refresh = () => {
      authAPI
        .me()
        .then(({ data }) => {
          if (!alive) return;
          setUser((prev) => {
            if (!prev) return prev;
            const next: User = {
              ...prev,
              role: data.role,
              fullName: data.fullName,
              isActive: data.isActive,
              departmentId: data.departmentId,
              departmentName: data.departmentName,
            };
            const changed =
              next.role !== prev.role ||
              next.fullName !== prev.fullName ||
              next.departmentId !== prev.departmentId ||
              next.departmentName !== prev.departmentName;
            if (!changed) return prev;
            if (next.role !== prev.role) {
              // Feature access defaults differ for admins and users.
              invalidateConnectorAccess();
              invalidateTaskAccess();
              invalidateWorkflowAccess();
            }
            localStorage.setItem('user', JSON.stringify(next));
            return next;
          });
        })
        .catch(() => {
          // Offline or a blip: keep what we have and try again later.
        });
    };
    refresh();
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, USER_REFRESH_MS);
    return () => {
      alive = false;
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, [token]);

  const login = async (email: string, password: string, rememberMe: boolean) => {
    const response = await authAPI.login({ email, password, rememberMe });
    const { token, user } = response.data;

    setToken(token);
    setUser(user);

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  };

  const logout = () => {
    authAPI.logout().catch(() => {});
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const clearMustChangePassword = () => {
    if (user) {
      const updated = { ...user, mustChangePassword: false };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    }
  };

  const isAdmin = user?.role === 'Admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, clearMustChangePassword, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

